import { FC, useCallback, useMemo } from "react";

import { convertStringToDate, formattedDateRange } from "src/js/util/dates";
import { TenureTypes } from "../../typings/Tenure";

import { FilterDateRange } from "./FilterDates";
import { FilterFloorArea } from "./FilterFloorArea";
import { FilterNewBuilds } from "./FilterNewBuilds";
import { FilterPropertyTypes, HiddenFieldPath } from "./FilterPropertyTypes";
import { FilterTenure } from "./FilterTenure";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { HMLRComparablesFilterOptions } from "../../typings/Comparables";
import { PropertyDesignationGQL, TenureShorthandGQL } from "react-migration/lib/typings/comparables/Comparables";
import { DisabledMessage, FiltersProps } from "./Filters";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { possibleUSCombinations, possibleUKCombinations } from "../../util/PropertyTypes";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";
import { HMLRDCSCondition, HMLRTenureShorthand } from "react-migration/lib/typings/comparables/HMLR";
import Feature from "src/js/stores/user/Feature";

export type HmlrFilterFields =
  | "dateFrom"
  | "dateTo"
  | "dcsConditions"
  | `dcsConditions.${HiddenFieldPath}`
  | "newBuildsOnly"
  | "tenure"
  | "floorArea"
  | "bedrooms"
  | "yearBuiltFrom"
  | "yearBuiltTo";

export const HmlrFilters: FC<FiltersProps<HmlrFilterFields, HMLRComparablesFilterOptions>> = ({
  disabled,
  disabledFields,
  filters,
  updateFilters,
  selectedDCSConditions,
  unitPreference,
  theme,
}) => {
  const { t } = useTranslation();

  const startDate = filters.dateFrom === null ? null : convertStringToDate(filters.dateFrom);
  const setStartDate = (date?: Date | null) => updateFilters({ dateFrom: date ? formattedDateRange(date) : null });

  const endDate = filters.dateTo === null ? null : convertStringToDate(filters.dateTo);
  const setEndDate = (date?: Date | null) => updateFilters({ dateTo: date ? formattedDateRange(date) : null });

  const setDCSConditions = useCallback(
    (dcsConditions: DCSCondition[]) => updateFilters({ dcsConditions: dcsConditions as HMLRDCSCondition[] }),
    [updateFilters]
  );
  const setShowNewBuildOnly = useCallback(
    (newBuildsOnly: boolean) => updateFilters({ newBuildsOnly }),
    [updateFilters]
  );
  const setTenureType = useCallback(
    (tenure: (TenureShorthandGQL | "Any")[]) => updateFilters({ tenure: tenure as HMLRTenureShorthand[] }),
    [updateFilters]
  );

  const setAreaMin = useCallback(
    (min: number) => {
      updateFilters({
        floorArea: {
          min: isNaN(Number(min)) || Number(min) === 0 ? undefined : (min as number),
          max: filters.floorArea?.max,
        },
      });
    },
    [filters, updateFilters]
  );

  const setAreaMax = useCallback(
    (max: number) => {
      updateFilters({
        floorArea: {
          min: filters.floorArea?.min,
          max: isNaN(Number(max)) ? undefined : (max as number),
        },
      });
    },
    [filters, updateFilters]
  );

  const usAccess = hasFeature(Feature.usAccess);
  const options = usAccess ? possibleUSCombinations : possibleUKCombinations;

  return (
    <div className="px-2 pb-2 flex flex-col text-xs md:text-sm">
      <DisabledMessage enableTooltip={disabledFields?.has("dcsConditions")}>
        <FilterPropertyTypes
          options={options}
          disabledFields={
            new Set(
              [...(disabledFields ?? [])]
                .filter((a) => /\.(Commercial|Residential|Other)/.test(a))
                .map((a) => a.replace(/^dcsConditions\./, "") as PropertyDesignationGQL)
            )
          }
          disabled={disabled || disabledFields?.has("dcsConditions")}
          selectedPropTypes={selectedDCSConditions}
          setSelectedPropTypes={setDCSConditions}
          theme={theme}
        />
      </DisabledMessage>

      <DisabledMessage enableTooltip={disabledFields?.has("tenure")}>
        <FilterTenure
          disabled={disabled || disabledFields?.has("tenure")}
          tenureTypes={filters.tenure}
          setTenureTypes={setTenureType}
          availableTenures={[TenureTypes.FREEHOLD, TenureTypes.LEASEHOLD]}
          theme={theme}
        />
      </DisabledMessage>
      <DisabledMessage enableTooltip={disabledFields?.has("floorArea")}>
        <FilterFloorArea
          disabled={disabled || disabledFields?.has("floorArea")}
          setAreaMin={setAreaMin}
          areaMin={filters.floorArea?.min}
          areaMax={filters.floorArea?.max}
          setAreaMax={setAreaMax}
          unitPreference={unitPreference}
          theme={theme}
        />
      </DisabledMessage>
      <DisabledMessage enableTooltip={disabledFields?.has("dateFrom") && disabledFields?.has("dateTo")}>
        <FilterDateRange
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          disabled={
            disabled || (disabledFields?.has("dateFrom") && disabledFields?.has("dateTo")) || filters.newBuildsOnly
          }
          label={t("comparables.map.sidebar.filters.filters.date")}
        />
      </DisabledMessage>
      <DisabledMessage enableTooltip={disabledFields?.has("newBuildsOnly")}>
        <FilterNewBuilds
          disabled={disabled || disabledFields?.has("newBuildsOnly")}
          showNewBuildOnly={!!filters.newBuildsOnly}
          setShowNewBuildOnly={setShowNewBuildOnly}
          theme={theme}
        />
      </DisabledMessage>
    </div>
  );
};

HmlrFilters.displayName = "HmlrFilters";
